import React, { useContext } from 'react'
import Linkify from 'react-linkify'
import { useIntl } from 'react-intl'

import { Maybe, TypeIdentifier } from '../../../common/types'
import { SectionTextColumn, Text } from '../../../components/layout'
import { TowingOrder } from '../../../containers/TowingOrderPage/ViewTowingOrder/types'
import { SummaryTowingRecord } from '../../../containers/TowingRecordPage/components/SummaryBody/types'
import { TowingRecord } from '../../../containers/TowingRecordPage/ViewTowingRecord/types'
import { toMultilineText } from '../../../util/common'
import { SectionBody, SectionHeader } from '../../layout/section'
import { translateCause, translateDeliverable } from '../../SosSubForm/util'
import { PhoneNumber } from '../../PhoneNumber'
import { StateStore } from '../../../containers/StoreProvider'
import { ArcCodeLists } from '../../ALSubForm/types'
import { getComponentType, getDescriptionType } from '../../ALSubForm/options'
import { formatMessageSafe } from '../../../util/translateInputOptions'
import { messages } from './messages'

type TowingObject = TowingOrder | TowingRecord | SummaryTowingRecord

export const BuildJobDetails = ({ towingObject }: { towingObject: TowingObject }) => {
  const {
    state: { AlArcCodes },
  } = useContext(StateStore)
  const intl = useIntl()
  const formatMessage = formatMessageSafe(intl)

  if (towingObject.typeIdentifier === TypeIdentifier.lahitapiola) {
    return buildLahitapiolaDetails(towingObject, formatMessage)
  }

  if (towingObject.typeIdentifier === TypeIdentifier.autoliitto) {
    return buildAutoliittoDetails(towingObject, AlArcCodes, formatMessage)
  }

  if (towingObject.typeIdentifier === TypeIdentifier.sos) {
    return buildSosDetails(towingObject, formatMessage)
  }

  return buildDetails(towingObject, formatMessage)
}

type FormatMessage = ReturnType<typeof formatMessageSafe>

const buildLahitapiolaDetails = (towingObject: TowingObject, formatMessage: FormatMessage) => {
  return (
    <>
      {towingObject.automobileAndTouringClubFinland && (
        <>
          {!towingObject.sosServiceOrder ? (
            <SectionTextColumn width="100%">
              <SectionHeader>
                <Text size="medium">{formatMessage(messages.towingReasonHeader)}</Text>
              </SectionHeader>
              <SectionBody>
                <Text size="medium">{towingObject.jobDetails.towingReason}</Text>
              </SectionBody>
            </SectionTextColumn>
          ) : (
            <SectionTextColumn width="100%">
              <SectionHeader>
                <Text size="medium">{formatMessage(messages.causeAndDeliverableHeader)}</Text>
              </SectionHeader>
              <SectionBody>
                <Text size="medium">
                  {translateCause(towingObject.sosServiceOrder.cause || towingObject.sosServiceOrder.estimatedCause)}
                </Text>
                <Text size="medium">
                  {translateDeliverable(
                    towingObject.sosServiceOrder.serviceType || towingObject.sosServiceOrder.estimatedServiceType
                  )}
                </Text>
              </SectionBody>
            </SectionTextColumn>
          )}
          <SectionTextColumn width="100%">
            <SectionHeader>
              <Text size="medium">{formatMessage(messages.referenceHeader)}</Text>
            </SectionHeader>
            <SectionBody>
              <Text size="medium">{towingObject.jobDetails.orderReference}</Text>
            </SectionBody>
          </SectionTextColumn>
          <SectionTextColumn width="100%">
            <SectionHeader>
              <Text size="medium">{formatMessage(messages.situationEstimateHeader)}</Text>
            </SectionHeader>
            <SectionBody>
              <Linkify>{toMultilineText(towingObject.automobileAndTouringClubFinland.situationDescription)}</Linkify>
            </SectionBody>
          </SectionTextColumn>
          <SectionTextColumn width="100%">
            <SectionHeader>
              <Text size="medium">{formatMessage(messages.billingHeader)}</Text>
            </SectionHeader>
            <SectionBody>
              <Text size="medium">{towingObject.automobileAndTouringClubFinland.billingInfo}</Text>
            </SectionBody>
          </SectionTextColumn>
        </>
      )}
    </>
  )
}

const buildAutoliittoDetails = (
  towingObject: TowingObject,
  arcCodes: Maybe<ArcCodeLists>,
  formatMessage: FormatMessage
) => {
  return (
    <>
      {towingObject.automobileAndTouringClubFinland && (
        <>
          <SectionTextColumn width="100%">
            <SectionHeader>
              <Text size="medium">{formatMessage(messages.arcFaultCodeHeader)}</Text>
            </SectionHeader>
            <SectionBody>
              <Text size="medium">
                {getComponentType(towingObject.automobileAndTouringClubFinland.arcCodes.component.code, arcCodes)}
              </Text>
              <Text size="medium">
                {getDescriptionType(towingObject.automobileAndTouringClubFinland.arcCodes.description.code, arcCodes)}
              </Text>
            </SectionBody>
          </SectionTextColumn>
          <SectionTextColumn width="100%">
            <SectionHeader>
              <Text size="medium">{formatMessage(messages.referenceHeader)}</Text>
            </SectionHeader>
            <SectionBody>
              <Text size="medium">{towingObject.automobileAndTouringClubFinland.caseNumber}</Text>
            </SectionBody>
          </SectionTextColumn>
          <SectionTextColumn width="100%">
            <SectionHeader>
              <Text size="medium">{formatMessage(messages.autoliittoSituationEstimateHeader)}</Text>
            </SectionHeader>
            <SectionBody>
              <Linkify>{toMultilineText(towingObject.automobileAndTouringClubFinland.situationDescription)}</Linkify>
            </SectionBody>
          </SectionTextColumn>
          <SectionTextColumn width="100%">
            <SectionHeader>
              <Text size="medium">{formatMessage(messages.serviceChannelAndBillingHeader)}</Text>
            </SectionHeader>
            <SectionBody>
              <Text size="medium">{towingObject.automobileAndTouringClubFinland.serviceChannel}</Text>
              <Text size="medium">{towingObject.automobileAndTouringClubFinland.billingInfo}</Text>
            </SectionBody>
          </SectionTextColumn>
        </>
      )}
    </>
  )
}

const buildSosDetails = (towingObject: TowingObject, formatMessage: FormatMessage) => {
  return (
    <>
      {towingObject.sosServiceOrder && (
        <>
          <SectionTextColumn width="100%">
            <SectionHeader>
              <Text size="medium">{formatMessage(messages.causeAndDeliverableHeader)}</Text>
            </SectionHeader>
            <SectionBody>
              <Text size="medium">
                {translateCause(towingObject.sosServiceOrder.cause || towingObject.sosServiceOrder.estimatedCause)}
              </Text>
              <Text size="medium">
                {translateDeliverable(
                  towingObject.sosServiceOrder.deliverable || towingObject.sosServiceOrder.estimatedServiceType
                )}
              </Text>
            </SectionBody>
          </SectionTextColumn>
          <SectionTextColumn width="100%">
            <SectionHeader>
              <Text size="medium">{formatMessage(messages.sosReferenceHeader)}</Text>
            </SectionHeader>
            <SectionBody>
              <Text size="medium">{towingObject.sosServiceOrder.caseNumber}</Text>
            </SectionBody>
          </SectionTextColumn>
          {towingObject.sosServiceOrder.originatorApp === 'LahiTapiola' ? (
            <SectionTextColumn width="100%">
              <SectionHeader>
                <Text size="medium">{formatMessage(messages.momentSituationEstimateHeader)}</Text>
              </SectionHeader>
              <SectionBody>{toMultilineText(towingObject.sosServiceOrder.incidentDescription)}</SectionBody>
            </SectionTextColumn>
          ) : (
            <SectionTextColumn width="100%">
              <SectionHeader>
                <Text size="medium">{formatMessage(messages.sosNotesHeader)}</Text>
              </SectionHeader>
              <SectionBody>{toMultilineText(towingObject.sosServiceOrder.situationDescription)}</SectionBody>
            </SectionTextColumn>
          )}
          <SectionTextColumn width="100%">
            <SectionHeader>
              <Text size="medium">{formatMessage(messages.conceptHeader)}</Text>
            </SectionHeader>
            <SectionBody>
              <Text size="medium">{towingObject.sosServiceOrder.agreementName}</Text>
            </SectionBody>
          </SectionTextColumn>
          <SectionTextColumn width="100%">
            <SectionHeader>
              <Text size="medium">{formatMessage(messages.jobInfoHeader)}</Text>
            </SectionHeader>
            <SectionBody>
              <Linkify>{toMultilineText(towingObject.jobDetails.additionalInfo)}</Linkify>
            </SectionBody>
          </SectionTextColumn>
          <SectionTextColumn width="100%">
            <SectionHeader>
              <Text size="medium">{formatMessage(messages.serviceCenterHeader)}</Text>
            </SectionHeader>
            <SectionBody>
              <Text size="medium">
                {towingObject.sosServiceOrder.originatorApp === 'LahiTapiola'
                  ? `${formatMessage(messages.serviceCenterMoment)} `
                  : `${formatMessage(messages.serviceCenterSos)} `}
                <PhoneNumber
                  size="medium"
                  phone={`${
                    towingObject.sosServiceOrder.originatorApp === 'LahiTapiola' ? '03-2173096' : '09-25271150'
                  }`}
                ></PhoneNumber>
              </Text>
            </SectionBody>
          </SectionTextColumn>
        </>
      )}
    </>
  )
}

const buildDetails = (towingObject: TowingObject, formatMessage: FormatMessage) => {
  return (
    <>
      <SectionTextColumn width="100%">
        <SectionHeader>
          <Text size="medium">{formatMessage(messages.towingReasonHeader)}</Text>
        </SectionHeader>
        <SectionBody>
          <Text size="medium">{towingObject.jobDetails.towingReason}</Text>
        </SectionBody>
      </SectionTextColumn>
      <SectionTextColumn width="100%">
        <SectionHeader>
          <Text size="medium">{formatMessage(messages.referenceHeader)}</Text>
        </SectionHeader>
        <SectionBody>
          <Text size="medium">{towingObject.jobDetails.orderReference}</Text>
        </SectionBody>
      </SectionTextColumn>
    </>
  )
}
